import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './GeneratePortalModal.css';
import axiosInstance from '../api';

export default function GeneratePortalModal({ onClose, onGenerated }) {
  const { t } = useTranslation();
  const [cpaName,    setCpaName]    = useState('');
  const [cpaEmail,   setCpaEmail]   = useState('');
  const [expiryDays, setExpiryDays] = useState(30);
  const [requireOtp, setRequireOtp] = useState(false);
  const [requireNda, setRequireNda] = useState(false);
  const [ndaText,    setNdaText]    = useState('');
  const [maxViews,   setMaxViews]   = useState('');
  const [loading,    setLoading]    = useState(false);
  const [portal,     setPortal]     = useState(null);
  const [copied,     setCopied]     = useState(false);

  const generate = () => {
    setLoading(true);
    axiosInstance.post('/auditor-portal/generate', {
      cpaName, cpaEmail, expiryDays,
      requireOtp: requireOtp && !!cpaEmail.trim(),
      requireNda,
      ndaText: requireNda ? ndaText : null,
      maxViews: maxViews ? Number(maxViews) : null,
    })
      .then(r => {
        setPortal(r.data);
        if (onGenerated) onGenerated(r.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const publicUrl = portal
    ? `${window.location.origin}/#/auditor/${portal.token}`
    : '';

  const copy = () => {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="gpm-overlay">
      <div className="gpm-modal">
        <div className="gpm-header">
          <div className="gpm-header-icon"><i className="fas fa-link" /></div>
          <div>
            <h3 className="gpm-title">{t('GeneratePortalModal.title')}</h3>
            <p className="gpm-sub">{t('GeneratePortalModal.subtitle')}</p>
          </div>
          <button className="gpm-close" onClick={onClose} aria-label={t('GeneratePortalModal.closeAria')}><i className="fas fa-times" /></button>
        </div>

        {!portal ? (
          <div className="gpm-body">
            <div className="gpm-field">
              <label className="gpm-label">{t('GeneratePortalModal.cpaNameLabel')}</label>
              <input className="gpm-input" placeholder={t('GeneratePortalModal.cpaNamePlaceholder')} value={cpaName}
                     onChange={e => setCpaName(e.target.value)} />
            </div>
            <div className="gpm-field">
              <label className="gpm-label">{t('GeneratePortalModal.cpaEmailLabel')}</label>
              <input className="gpm-input" type="email" placeholder="auditor@cpafirm.com"
                     value={cpaEmail} onChange={e => setCpaEmail(e.target.value)} />
            </div>
            <div className="gpm-field">
              <label className="gpm-label">{t('GeneratePortalModal.linkExpiryLabel')}</label>
              <div className="gpm-expiry-btns">
                {[7, 14, 30].map(d => (
                  <button key={d}
                          className={`gpm-expiry-btn ${expiryDays === d ? 'active' : ''}`}
                          onClick={() => setExpiryDays(d)}>
                    {t('GeneratePortalModal.expiryDays', { count: d })}
                  </button>
                ))}
              </div>
            </div>
            <div className="gpm-field">
              <label className="gpm-label">{t('GeneratePortalModal.accessControlsLabel', 'Access controls')}</label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#334155', marginBottom: 8 }}>
                <input type="checkbox" checked={requireOtp} disabled={!cpaEmail.trim()}
                       onChange={e => setRequireOtp(e.target.checked)} style={{ marginTop: 3 }} />
                <span>{t('GeneratePortalModal.requireOtp', 'Require email verification — a one-time code is sent to the auditor before they can view')}
                  {!cpaEmail.trim() && <em style={{ color: '#94a3b8' }}> {t('GeneratePortalModal.requireOtpNeedsEmail', '(add an email above)')}</em>}</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#334155', marginBottom: 8 }}>
                <input type="checkbox" checked={requireNda} onChange={e => setRequireNda(e.target.checked)} style={{ marginTop: 3 }} />
                <span>{t('GeneratePortalModal.requireNda', 'Require NDA / confidentiality acceptance before viewing')}</span>
              </label>
              {requireNda && (
                <textarea className="gpm-input" rows={3} value={ndaText} onChange={e => setNdaText(e.target.value)}
                          placeholder={t('GeneratePortalModal.ndaPlaceholder', 'Confidentiality notice shown to the auditor…')}
                          style={{ marginBottom: 8 }} />
              )}
              <input className="gpm-input" type="number" min="1" value={maxViews}
                     onChange={e => setMaxViews(e.target.value)}
                     placeholder={t('GeneratePortalModal.maxViewsPlaceholder', 'Maximum views (optional)')} />
            </div>
            <div className="gpm-info">
              <i className="fas fa-info-circle" />
              {t('GeneratePortalModal.infoNote')}
            </div>
            <div className="gpm-footer">
              <button className="gpm-cancel" onClick={onClose}>{t('GeneratePortalModal.cancel')}</button>
              <button className="gpm-generate" onClick={generate} disabled={loading || !cpaName.trim()}>
                {loading ? <><i className="fas fa-spinner fa-spin" /> {t('GeneratePortalModal.generating')}</> : t('GeneratePortalModal.generateLink')}
              </button>
            </div>
          </div>
        ) : (
          <div className="gpm-body">
            <div className="gpm-success">
              <div className="gpm-success-icon"><i className="fas fa-check-circle" /></div>
              <div className="gpm-success-title">{t('GeneratePortalModal.successTitle')}</div>
              <div className="gpm-success-sub">
                {t('GeneratePortalModal.shareWith')} <strong>{portal.cpaName}</strong>.
                {' '}{t('GeneratePortalModal.expiresOn', { date: new Date(portal.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) })}
              </div>
            </div>
            <div className="gpm-link-box">
              <div className="gpm-link-url">{publicUrl}</div>
              <button className="gpm-copy-btn" onClick={copy}>
                {copied ? <><i className="fas fa-check" /> {t('GeneratePortalModal.copied')}</> : <><i className="fas fa-copy" /> {t('GeneratePortalModal.copy')}</>}
              </button>
            </div>
            <div className="gpm-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="gpm-generate" onClick={onClose}>{t('GeneratePortalModal.done')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
